import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, FileText, History, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { authorizedWorkspaces } from '@/lib/workspaces';
import { AppHubButton } from '@/components/common/AppHubButton';
import { MiamiHqFooter } from '@/components/common/MiamiHqFooter';
import AppList from './field/AppList';
import FormSubmission from './field/FormSubmission';
import SubmissionHistory from './field/SubmissionHistory';
import WorkOrderForm from './field/WorkOrderForm';
import WorkOrderList from './field/WorkOrderList';
import VehicleInspectionForm from './field/VehicleInspectionForm';
import VehicleInspectionList from './field/VehicleInspectionList';
import PropertyEvaluationForm from './field/PropertyEvaluationForm';
import PropertyEvaluationList from './field/PropertyEvaluationList';
import BankPMInspectionForm from './field/BankPMInspectionForm';
import BankPMInspectionList from './field/BankPMInspectionList';
import IncidentReportForm from './field/IncidentReportForm';
import IncidentReportList from './field/IncidentReportList';
import IncidentReportDetail from './field/IncidentReportDetail';
import PMPSchoolsForm from './field/PMPSchoolsForm';
import PMPSchoolsList from './field/PMPSchoolsList';
import PMPSchoolsDetail from './field/PMPSchoolsDetail';
import BuildingIntelligenceForm from './field/BuildingIntelligenceForm';
import BuildingIntelligenceList from './field/BuildingIntelligenceList';
import BuildingIntelligenceDetail from './field/BuildingIntelligenceDetail';
import PMPOfficeForm from './field/PMPOfficeForm';
import PMPOfficeList from './field/PMPOfficeList';
import PMPOfficeDetail from './field/PMPOfficeDetail';
import WorkOrderCompletionForm from './field/WorkOrderCompletionForm';
import InspectionForm from './field/InspectionForm';
import InspectionList from './field/InspectionList';
import TemplateBasedInspectionForm from './field/TemplateBasedInspectionForm';
import FieldWorkOrders from './field/wom/FieldWorkOrders';

const FieldPortal = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 50%, #A5D6A7 100%)' }}>
      <MiamiHqFooter />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
                alt="TFS Logo" 
                className="h-12"
              />
              <div>
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TFS Field Portal</h1>
                <p className="text-xs text-slate-500">Usuario de Campo / Field User</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{user?.name}</p>
              </div>
              {authorizedWorkspaces(user).length > 1 && (
                <AppHubButton variant="light" />
              )}
              <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6 flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field')}
            data-testid="nav-apps"
          >
            <FileText className="h-4 w-4" />
            <span>Apps</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-[#0E2A47] text-white hover:bg-[#16324F] hover:text-white"
            onClick={() => navigate('/field/my-work')}
            data-testid="nav-my-work"
          >
            <FileText className="h-4 w-4" />
            <span>My Work Orders</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/inspections/list')}
            data-testid="nav-inspections"
          >
            <FileText className="h-4 w-4" />
            <span>Inspections</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/vehicle-inspections')}
            data-testid="nav-vehicle"
          >
            <FileText className="h-4 w-4" />
            <span>Vehicle</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/property-evaluations')}
            data-testid="nav-evaluations"
          >
            <FileText className="h-4 w-4" />
            <span>Property</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/bank-pm-inspections')}
            data-testid="nav-bank-pm"
          >
            <FileText className="h-4 w-4" />
            <span>Bank PM</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/pmp-schools')}
            data-testid="nav-pmp-schools"
          >
            <FileText className="h-4 w-4" />
            <span>PMP Schools</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/pmp-office')}
            data-testid="nav-pmp-office"
          >
            <FileText className="h-4 w-4" />
            <span>PMP Office</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/incident-reports')}
            data-testid="nav-incidents"
          >
            <FileText className="h-4 w-4" />
            <span>Incidents</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/building-intelligence')}
            data-testid="nav-building-intelligence"
          >
            <FileText className="h-4 w-4" />
            <span>Building Intel</span>
          </Button>
          <Button
            variant="outline"
            className="flex items-center space-x-2 bg-white"
            onClick={() => navigate('/field/history')}
            data-testid="nav-history"
          >
            <History className="h-4 w-4" />
            <span>History</span>
          </Button>
        </div>

        {/* Content Area */}
        <Routes>
          <Route path="/" element={<AppList />} />
          <Route path="/my-work" element={<FieldWorkOrders />} />
          <Route path="/app/:appId" element={<FormSubmission />} />
          <Route path="/history" element={<SubmissionHistory />} />
          <Route path="/work-orders" element={<WorkOrderList />} />
          <Route path="/work-order/new" element={<WorkOrderForm />} />
          <Route path="/work-order/complete" element={<WorkOrderCompletionForm />} />
          <Route path="/vehicle-inspections" element={<VehicleInspectionList />} />
          <Route path="/vehicle-inspection/new" element={<VehicleInspectionForm />} />
          <Route path="/property-evaluations" element={<PropertyEvaluationList />} />
          <Route path="/property-evaluation/new" element={<PropertyEvaluationForm />} />
          <Route path="/bank-pm-inspections" element={<BankPMInspectionList />} />
          <Route path="/bank-pm-inspection/new" element={<BankPMInspectionForm />} />
          <Route path="/pmp-schools" element={<PMPSchoolsList />} />
          <Route path="/pmp-schools/new" element={<PMPSchoolsForm />} />
          <Route path="/pmp-schools/:inspectionId" element={<PMPSchoolsDetail />} />
          <Route path="/pmp-office" element={<PMPOfficeList />} />
          <Route path="/pmp-office/new" element={<PMPOfficeForm />} />
          <Route path="/pmp-office/:inspectionId" element={<PMPOfficeDetail />} />
          <Route path="/incident-reports" element={<IncidentReportList />} />
          <Route path="/incident-report/new" element={<IncidentReportForm />} />
          <Route path="/incident-reports/:reportId" element={<IncidentReportDetail />} />
          <Route path="/building-intelligence" element={<BuildingIntelligenceList />} />
          <Route path="/building-intelligence/new" element={<BuildingIntelligenceForm />} />
          <Route path="/building-intelligence/:intelligenceId" element={<BuildingIntelligenceDetail />} />
          <Route path="/inspections/list" element={<InspectionList />} />
          <Route path="/inspections/new" element={<InspectionForm />} />
          <Route path="/inspections/template-based" element={<TemplateBasedInspectionForm />} />
        </Routes>
      </div>
    </div>
  );
};

export default FieldPortal;